import * as dashboardService from '../../../services/Dashboard';
import { get, isString } from 'lodash';
import { notification } from 'antd';
import axios from 'axios';
import moment from 'moment';
import {
  ButtonConstant,
  CodeConstant,
  MessageConstant,
  ApiConstant
} from '../../../constants';
import { pushLog2DV2LogServer } from '../../../services/logging';
import { ProfileActions as PAConstants } from './constants';
import { getAvailabilityId } from '../../../constants/AvailabilityType';

const NEED_QUICK_REMATCH = 'need quick rematch';
const NOT_AVAILABLE = 'not available';

const LOAD_BASE_PROFILE_DATA = (dispatch, attemptNumber = 1) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: token
  };
  const requests = [];
  requests.push(dashboardService.getAllBaseProfileData());
  requests.push(
    dashboardService.getDeveloperAvailabilityStatuses().then((res) => ({
      data: { data: { developerAvailabilityStatuses: res.data } }
    }))
  );
  requests.push(
    dashboardService.getCurrencyRateList().then((res) => ({
      data: {
        data: {
          rate_config: get(res, 'data.data.rateList', []),
          rate_config_date: new Date().getDate()
        }
      }
    }))
  );
  requests.push(
    axios
      .post(
        `${ApiConstant.BASE_URL}common/`,
        { action: 'GET_JOB_ROLE_TYPE' },
        { headers }
      )
      .then((res) => {
        const jobRoles = get(res, 'data.result', []).map((data) => {
          return {
            label: data.role_type,
            value: data.id
          };
        });
        // Move other role type to the end of the array
        const otherIndex = jobRoles.findIndex((item) => item.label.toLowerCase() === 'other');
        if (otherIndex >= 0) jobRoles.push(jobRoles.splice(otherIndex, 1)[0]);
        return { data: { data: { jobRoles } } };
      })
  );
  requests.push(
    axios
      .post(
        `${ApiConstant.BASE_URL}common-user/`,
        { action: 'GET_USER_SALARY_TYPE' },
        { headers }
      )
      .then((res) => {
        const salaryType = get(res, 'data.result', []).reduce((type, data) => {
          return data.randomValue;
        }, 3);
        return { data: { data: { salaryType } } };
      })
  );
  dispatch({ type: ProfileActions.LOADING_PROFILE_DATA });
  Promise.all(requests)
    .then((result) => result.map((res) => res.data && res.data.data))
    .then((result) => result.reduce((acc, data) => ({ ...acc, ...data }), {}))
    .then((data) => {
      dispatch({
        type: ProfileActions.LOAD_PROFILE_DATA_SUCCESS,
        payload: data,
        firstProfileLoaded: true /* COMMENT IN Profile.reducer.js */
      });
    })
    .catch((error) => {
      if (!Number.isInteger(attemptNumber) || attemptNumber <= 5) {
        setTimeout(() => {
          LOAD_BASE_PROFILE_DATA(dispatch, Number.isInteger(attemptNumber) ? attemptNumber + 1 : 2);
        }, 1000);
      } else {
        dispatch({
          type: ProfileActions.LOAD_PROFILE_DATA_FAILED,
          error,
          message: 'Failed to load profile data'
        });
      }
    });
};

const LOAD_PROFILE_DATA = (dispatch, attemptNumber = 1) => {
  const requests = [];
  requests.push(dashboardService.getAllUserProfileData());
  dispatch({ type: ProfileActions.LOADING_PROFILE_DATA });
  Promise.all(requests)
    .then((result) => result.map((res) => res.data && res.data.data))
    .then((result) => result.reduce((acc, data) => ({ ...acc, ...data }), {}))
    .then((data) => {
      dispatch({
        type: ProfileActions.LOAD_PROFILE_DATA_SUCCESS,
        payload: data
      });
    })
    .catch((error) => {
      if (!Number.isInteger(attemptNumber) || attemptNumber <= 5) {
        setTimeout(() => {
          LOAD_PROFILE_DATA(dispatch, Number.isInteger(attemptNumber) ? attemptNumber + 1 : 2);
        }, 1000);
      } else {
        dispatch({
          type: ProfileActions.LOAD_PROFILE_DATA_FAILED,
          error,
          message: 'Failed to load profile data'
        });
      }
    });
};

const LOAD_PARSED_RESUME_DATA = (dispatch, attemptNumber = 1) => {
  const requests = [];
  requests.push(dashboardService.getParsedResumeData());
  dispatch({ type: ProfileActions.LOADING_PROFILE_DATA });
  Promise.all(requests)
    .then((result) => result.map((res) => res.data && res.data.data))
    .then((result) => result.reduce((acc, data) => ({ ...acc, ...data }), {}))
    .then((data) => {
      dispatch({
        type: ProfileActions.LOAD_PARSED_RESUME_DATA_SUCCESS,
        payload: data
      });
    })
    .catch((error) => {
      console.error(error);
      if (!Number.isInteger(attemptNumber) || attemptNumber <= 5) {
        setTimeout(() => {
          LOAD_PARSED_RESUME_DATA(dispatch, Number.isInteger(attemptNumber) ? attemptNumber + 1 : 2);
        }, 1000);
      }
    });
};

const LOAD_USER_AVAILABILITY = (dispatch) => {
  dashboardService
    .getUserAvailabilityV2()
    .then((res) => {
      const data = res.data.data;
      if (data) {
        const availabilityStatusId = getAvailabilityId(data.developer_availability_type);
        const current_availability_status = data.developer_availability_type || '';
        const availability_answer = data.availability_answer;
        const last_update = data.last_update;
        const starts_in_weeks = data.starts_in_weeks;

        dispatch({
          type: ProfileActions.SET_USER_AVAILABILITY,
          payload: {
            disabledEditAvailability: [NEED_QUICK_REMATCH].includes(current_availability_status.toLowerCase().trim()),
            current_availability_status,
            availabilityStatusId,
            availability_answer,
            last_update,
            starts_in_weeks,
            userAvailability: availabilityStatusId && ![NEED_QUICK_REMATCH, NOT_AVAILABLE].includes(current_availability_status.toLowerCase().trim())
          }
        });
      }
    })
    .catch((error) => {
      dispatch({
        type: ProfileActions.LOAD_PROFILE_DATA_FAILED,
        error,
        message: 'Failed to load availability infos'
      });
    });
};

const UPDATE_AVATAR = (data, callback) => (dispatch) => {
  let promiseObj;

  if (data.dialogState === ButtonConstant.DIALOG_EDIT) {
    promiseObj = dashboardService.saveAvatar(data.avatorFile);
  } else if (data.dialogState === ButtonConstant.DIALOG_DELETE) {
    promiseObj = dashboardService.deleteAvatar();
  } else {
    return;
  }

  dispatch({ type: ProfileActions.LOADING_PROFILE_DATA });

  return promiseObj
    .then((res) => {
      if (res.data.code === CodeConstant.OK) {
        dispatch(ProfileActions.LOAD_PROFILE_DATA);
        openNotification('success', 'Avatar successfully updated');
        pushLog2DV2LogServer.info('PROFILE_LOGGING', 'UPDATE_AVATAR', { }, { }, null);
      } else {
        const errorMessage = isString(res.data)
          ? res.data
          : MessageConstant.CALL_API_ERROR;
        openNotification('error', 'Error', errorMessage);
        pushLog2DV2LogServer.error('PROFILE_LOGGING', 'UPDATE_AVATAR', { }, { }, errorMessage);
      }
    })
    .catch((error) => {
      openNotification('error', 'Failed to update the avatar');
      console.error(error);
    })
    .finally(() => {
      dispatch({ type: ProfileActions.END_LOADING });
      if (callback) callback();
    });
};

const UPDATE_PROFILE_DATA = (data, next, isAutoSave = false) => (dispatch) => {
  dispatch({ type: ProfileActions.LOADING_PROFILE_DATA });

  dashboardService
    .updateProfileData(data)
    .then((res) => {
      dispatch(ProfileActions.LOAD_PROFILE_DATA);
      if (!isAutoSave) openNotification('success', 'Information successfully updated');
      localStorage.setItem('userFullName', data.full_name || data.first_name + ' ' + data.lastName);
      pushLog2DV2LogServer.info('PROFILE_LOGGING', 'UPDATE_PROFILE_DATA', { }, data, null);
      if (next) next(true);
    })
    .catch((error) => {
      if (!isAutoSave) openNotification('error', 'Failed to update the information');
      pushLog2DV2LogServer.error('PROFILE_LOGGING', 'UPDATE_PROFILE_DATA', { }, data, error);
      if (next) next(false);
      console.error(error);
    })
    .finally(() => dispatch({ type: ProfileActions.END_LOADING }));
};

const UPDATE_DEVELOPER_AVAILABILITY = (userInfo, callback) => (dispatch) => {
  dispatch({ type: ProfileActions.LOADING_PROFILE_DATA });
  const data = {
    availabilityStatusId: userInfo.userAvailability
      ? userInfo.availability_status_id
      : 1,
    startsInWeeks:
    ((!userInfo.starts_in_weeks && userInfo.starts_in_weeks !== 0) || userInfo.starts_in_weeks === -1) ? 5 : userInfo.starts_in_weeks
  };

  dashboardService
    .saveDeveloperAvailability(data)
    .then((res) => {
      dispatch(ProfileActions.LOAD_PROFILE_DATA);
      dispatch(ProfileActions.LOAD_USER_AVAILABILITY);
      openNotification('success', 'Availability successfully updated');
      pushLog2DV2LogServer.info('PROFILE_LOGGING', 'UPDATE_DEVELOPER_AVAILABILITY', { }, data, null);
      callback();
    })
    .catch((error) => {
      openNotification('error', 'Availability update failed');
      pushLog2DV2LogServer.error('PROFILE_LOGGING', 'UPDATE_DEVELOPER_AVAILABILITY', { }, data, error);
      callback();
      console.error(error);
    })
    .finally(() => dispatch({ type: ProfileActions.END_LOADING }));
};

const UPDATE_DEVELOPER_SKILLS = (skillsChanges, callback) => (dispatch) => {
  if (!skillsChanges.skills) {
    if (callback) callback();
    return;
  }
  dispatch({ type: ProfileActions.LOADING_PROFILE_DATA });

  dashboardService
    .updateDeveloperSkills(skillsChanges.skills)
    .then((res) => {
      dispatch(ProfileActions.LOAD_PROFILE_DATA);
      openNotification('success', 'Skills successfully updated');
      pushLog2DV2LogServer.info('PROFILE_LOGGING', 'UPDATE_DEVELOPER_SKILLS', { }, skillsChanges, null);
      callback();
    })
    .catch((error) => {
      openNotification('error', 'Skills update failed');
      pushLog2DV2LogServer.error('PROFILE_LOGGING', 'UPDATE_DEVELOPER_SKILLS', { }, skillsChanges, error);
      callback();
      console.error(error);
    })
    .finally(() => dispatch({ type: ProfileActions.END_LOADING }));
};

const montExperienceProjects = (data) => {
  const dataForm = new FormData();
  dataForm.append('cPosition', data.cPosition);
  dataForm.append('cCompany', data.cCompany);
  dataForm.append('cDetails', data.cDetails);
  dataForm.append('cStartYear', moment(data.cStart).format('YYYY'));
  dataForm.append('cStartMonth', moment(data.cStart).format('MMMM'));
  dataForm.append(
    'cEndYear',
    data.cStillWork ? 0 : moment(data.cEnd).format('YYYY')
  );
  dataForm.append(
    'cEndMonth',
    data.cStillWork ? null : moment(data.cEnd).format('MMMM')
  );
  data.projectKeys.map((index) => {
    const files = data.files[index]
      ? data.files[index].filter((item) => item.name)
      : [];
    dataForm.append('project_index', index);
    dataForm.append(`project_name-${index}`, data.project_name[index]);
    dataForm.append(`project_details-${index}`, data.project_details[index]);
    // dataForm.append(`project_contribution-${index}`, data.project_contribution[index]);
    dataForm.append(`project_outcome-${index}`, data.project_outcome[index]);
    dataForm.append(
      `project_url-${index}`,
      data.project_url[index] ? data.project_url[index] : ''
    );
    if (data.project_skills[index].length) {
      data.project_skills[index].map((skill) => {
        dataForm.append(`project_skills-${index}`, skill);
      });
    }
    if (files.length) {
      files.map((file) => {
        dataForm.append(`project_files-${index}`, file);
      });
    }
    if (data.filesDeleted[index] && data.filesDeleted[index].length) {
      data.filesDeleted[index].map((file) => {
        dataForm.append(`project_filesDeleted-${index}`, JSON.stringify(file));
      });
    }
    if (data.project_id && data.project_id[index]) { dataForm.append(`project_id-${index}`, data.project_id[index]); }
  });
  if (data.projectsDeleted.length) {
    data.projectsDeleted.map((project_id) => {
      dataForm.append('project_deleted', project_id);
    });
  }
  return dataForm;
};

const UPDATE_EXP_INFO = (values, callback = null) => (dispatch) => {
  let promiseObj;
  dispatch({ type: ProfileActions.LOADING_PROFILE_DATA });
  if (values.dialogState === ButtonConstant.DIALOG_ADD) {
    promiseObj = dashboardService.saveExperienceInfo(
      montExperienceProjects(values)
    );
  } else if (values.dialogState === ButtonConstant.DIALOG_EDIT) {
    promiseObj = dashboardService.updateExperienceInfo(
      montExperienceProjects(values),
      values.cId
    );
  } else if (values.dialogState === ButtonConstant.DIALOG_DELETE) {
    promiseObj = dashboardService.deleteExperienceInfo(values);
  } else {
    return;
  }
  promiseObj
    .then((res) => {
      dispatch(ProfileActions.LOAD_PROFILE_DATA);
      openNotification('success', 'Experiences list successfully updated');
      pushLog2DV2LogServer.info('PROFILE_LOGGING', 'UPDATE_EXP_INFO', { }, values, null);
      if (callback) callback();
    })
    .catch((error) => {
      openNotification('error', 'Experiences list failed to update');
      pushLog2DV2LogServer.error('PROFILE_LOGGING', 'UPDATE_EXP_INFO', { }, values, error);
      if (callback) callback();
      console.error(error);
    })
    .finally(() => dispatch({ type: ProfileActions.END_LOADING }));
};

const SAVE_PROJECT_CHANGES = (changes, callback = null) => (dispatch) => {
  dispatch({ type: ProfileActions.LOADING_PROFILE_DATA });

  dashboardService
    .saveProjectsChanges(changes)
    .then((res) => {
      dispatch(ProfileActions.LOAD_PROFILE_DATA);
      openNotification('success', 'Projects list successfully updated');
      pushLog2DV2LogServer.info('PROFILE_LOGGING', 'SAVE_PROJECT_CHANGES', { }, changes, null);
      if (callback) callback();
    })
    .catch((error) => {
      openNotification('error', 'Failed to save the projects changes');
      pushLog2DV2LogServer.error('PROFILE_LOGGING', 'SAVE_PROJECT_CHANGES', { }, changes, error);
      callback();
      console.error(error);
    })
    .finally(() => dispatch({ type: ProfileActions.END_LOADING }));
};

const SAVE_EDUCATION_CHANGES = (changes, callback = null) => (dispatch) => {
  dispatch({ type: ProfileActions.LOADING_PROFILE_DATA });
  dashboardService
    .saveEducationChanges(changes)
    .then((res) => {
      dispatch(ProfileActions.LOAD_PROFILE_DATA);
      openNotification('success', 'Educations list successfully updated');
      pushLog2DV2LogServer.info('PROFILE_LOGGING', 'SAVE_EDUCATION_CHANGES', { }, changes, null);
      if (callback) callback();
    })
    .catch((error) => {
      openNotification('error', 'Failed to save the eduction changes');
      pushLog2DV2LogServer.error('PROFILE_LOGGING', 'SAVE_EDUCATION_CHANGES', { }, changes, error);
      callback();
      console.error(error);
    })
    .finally(() => dispatch({ type: ProfileActions.END_LOADING }));
};

const SAVE_PUBLICATIONS_CHANGES = (changes, callback = null) => (dispatch) => {
  dispatch({ type: ProfileActions.LOADING_PROFILE_DATA });

  dashboardService
    .savePublicationsChanges(changes)
    .then((res) => {
      dispatch(ProfileActions.LOAD_PROFILE_DATA);
      openNotification('success', 'Publications list successfully updated');
      pushLog2DV2LogServer.info('PROFILE_LOGGING', 'SAVE_PUBLICATIONS_CHANGES', { }, changes, null);
      if (callback) callback();
    })
    .catch((error) => {
      openNotification('error', 'Failed to save the publication changes');
      pushLog2DV2LogServer.error('PROFILE_LOGGING', 'SAVE_PUBLICATIONS_CHANGES', { }, changes, error);
      callback();
      console.error(error);
    })
    .finally(() => dispatch({ type: ProfileActions.END_LOADING }));
};

const SAVE_CERTIFICATIONS_CHANGES = (changes, callback = null) => (
  dispatch
) => {
  dispatch({ type: ProfileActions.LOADING_PROFILE_DATA });

  dashboardService
    .saveCertificationsChanges(changes)
    .then((res) => {
      dispatch(ProfileActions.LOAD_PROFILE_DATA);
      dispatch(ProfileActions.LOAD_BASE_PROFILE_DATA);
      openNotification('success', 'Certifications list successfully updated');
      pushLog2DV2LogServer.info('PROFILE_LOGGING', 'SAVE_CERTIFICATIONS_CHANGES', { }, changes, null);
      if (callback) callback();
    })
    .catch((error) => {
      openNotification('error', 'Failed to save the certification changes');
      pushLog2DV2LogServer.error('PROFILE_LOGGING', 'SAVE_CERTIFICATIONS_CHANGES', { }, changes, error);
      callback();
      console.error(error);
    })
    .finally(() => dispatch({ type: ProfileActions.END_LOADING }));
};

const SAVE_RESUME = (file, callback = null) => (
  dispatch
) => {
  dispatch({ type: ProfileActions.LOADING_PROFILE_DATA });

  dashboardService
    .saveResume(file)
    .then((res) => {
      dispatch(ProfileActions.LOAD_PROFILE_DATA);
      openNotification('success', 'Resume successfully uploaded');
      pushLog2DV2LogServer.info('PROFILE_LOGGING', 'SAVE_RESUME', { }, { }, null);
      if (callback) callback();
    })
    .catch((error) => {
      openNotification('error', 'Failed to upload the file');
      pushLog2DV2LogServer.error('PROFILE_LOGGING', 'SAVE_RESUME', { }, { }, error);
      callback();
      console.error(error);
    })
    .finally(() => dispatch({ type: ProfileActions.END_LOADING }));
};

export const openNotification = (type, title, description) => {
  notification[type]({
    message: title,
    description: description,
    duration: 5
  });
};

const ProfileActions = {
  LOAD_PROFILE_DATA,
  LOAD_BASE_PROFILE_DATA,
  LOAD_USER_AVAILABILITY,
  UPDATE_AVATAR,
  UPDATE_PROFILE_DATA,
  UPDATE_DEVELOPER_AVAILABILITY,
  UPDATE_DEVELOPER_SKILLS,
  UPDATE_EXP_INFO,
  SAVE_PROJECT_CHANGES,
  SAVE_EDUCATION_CHANGES,
  SAVE_PUBLICATIONS_CHANGES,
  SAVE_CERTIFICATIONS_CHANGES,
  SAVE_RESUME,
  LOAD_PARSED_RESUME_DATA,
  ...PAConstants
};

export default ProfileActions;
